import { z } from 'zod'

import { isCalendarDate } from '@/lib/time'

/** The views the calendar has. Month is not built; the param shape leaves room for it. */
export const CALENDAR_VIEWS = ['day', 'week'] as const
export type CalendarView = (typeof CALENDAR_VIEWS)[number]

/** Day, as the phone opens on. Never written to the URL, so a shared link says what it means. */
export const DEFAULT_VIEW: CalendarView = 'day'

/** The named staff filters; anything else in `staff` is a member's user id. */
export const STAFF_FILTERS = ['mine', 'unassigned', 'all'] as const

/**
 * Route search params for the calendar. Every field falls back on its own, so a mangled shared
 * link (`?view=month&kind=lease`) still opens the calendar and drops only the values it cannot
 * read. `date` is absent for "today", which is a different day for each reader by design.
 *
 * The week grid, the "Didn't start" filter and the booking sheet all read this schema; add to it
 * rather than parsing the URL anywhere else.
 */
export const calendarSearchSchema = z
  .object({
    date: z.string().refine(isCalendarDate).optional().catch(undefined),
    view: z.enum(CALENDAR_VIEWS).optional().catch(undefined),
    // `mine`, `unassigned`, `all`, or the id of one member.
    staff: z
      .union([z.enum(STAFF_FILTERS), z.uuid()])
      .optional()
      .catch(undefined),
    kind: z.enum(['test_drive', 'loan']).optional().catch(undefined),
    // One vehicle, by id: what narrows the week grid's rows in place of virtualising them.
    vehicle: z.uuid().optional().catch(undefined),
    /**
     * "Didn't start": only the bookings still `booked` whose window has passed. Off is an absent
     * key rather than `false`, the same way `all` and today are, so a shared link carries only
     * what was actually narrowed — which is why the only value it holds is `true`.
     *
     * The router's default search parser reads `?unresolved=true` as a boolean; the string form
     * is accepted too, so a link typed by hand under another parser still opens narrowed.
     */
    unresolved: z
      .union([z.literal(true), z.literal('true')])
      .transform(() => true as const)
      .optional()
      .catch(undefined),
  })
  // A search string that is not an object at all (`?date[]=a&date[]=b`) opens the default view.
  .catch({})

export type CalendarSearch = z.infer<typeof calendarSearchSchema>
